import '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle.scss';

import View                              from '@widesk-ui/components/View';
import NormalHoverToggle                 from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import useHoverPreview                   from '@/TokamapEditor/helpers/useHoverPreview';
import { type NormalHoverScope }         from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import TextContentField                  from '@/TokamapEditor/ux/AreaSidebar/TextContentField';
import Field                             from '@/TokamapEditor/ux/AreaSidebar/Field';
import Button                            from '@widesk-ui/components/Button';
import currentEditorStore                from '@/TokamapEditor/stores/currentEditorStore';
import React                             from 'react';
import SliderInputNumberFeature          from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumberFeature';
import { observer }                      from 'mobx-react-lite';
import { commonValue }                   from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import useDesignTokens                   from '@/hooks/useDesignTokens';
import { themeTextLock }                 from '@/TokamapEditor/helpers/themeImposed';
import { ThemeLockTag }                  from '@/TokamapEditor/ux/AreaSidebar/ThemeLocked';
import { ThemeLockedControl }            from '@/TokamapEditor/ux/AreaSidebar/ThemeLocked';
import { DEFAULT_TEXT_FIXED_SIZE_PX }    from '@/TokamapEditor/ux/constants';
import { SLIDER_MAX_TEXT_FIXED_SIZE_PX } from '@/TokamapEditor/ux/constants';
import { MAX_TEXT_FIXED_SIZE_PX }        from '@/TokamapEditor/ux/constants';
import type { DrawTriggerOptions }       from '@tokamap/core';
import type { ShapeStyle }               from '@tokamap/core';
import type { ShapeText }                from '@tokamap/core';
import type { ShapeTextStyle }           from '@tokamap/core';
import Segmented                         from '@widesk-ui/Segmented';
import ToolbarToggle                     from '@/TokamapEditor/ux/AreaSidebar/ToolbarToggle';
import ColorButton                       from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import CollapsiblePanel                  from '@/TokamapEditor/ux/AreaSidebar/CollapsiblePanel';
import OrientationField                  from '@/TokamapEditor/ux/AreaSidebar/OrientationField';
import { orientationModeOf }             from '@/TokamapEditor/ux/AreaSidebar/orientation';
import PositionGrid                      from '@/TokamapEditor/ux/AreaSidebar/PositionGrid';
import shapePlaceId                      from '@/TokamapEditor/helpers/shapePlaceId';
import { placeLabel }                    from '@/TokamapEditor/helpers/placeLinkedContent';
import { PLACE_LABEL_SOURCE }            from '@/TokamapEditor/helpers/placeLinkedContent';
import { showsBadge }                    from '@/TokamapEditor/helpers/shapeBadge';
import TextFontSelect                    from '@/TokamapEditor/ux/TextFontSelect';

const STATE_NOTES: Record<NormalHoverScope, string> = {
	normal: `Le contenu et les couleurs ci-dessous s'appliquent à l'état normal.`,
	hover: `Le contenu et les couleurs ci-dessous s'appliquent quand le visiteur survole la forme.`,
};

const AreaTextSection = observer(() => {
	const [scope, setScope] = React.useState<NormalHoverScope>('normal');
	const badged = showsBadge(currentEditorStore.sidebarShapes);
	const hover = scope === 'hover' && !badged;

	useHoverPreview(hover);

	return (
		<View gap="md">
			{!badged && <NormalHoverToggle value={scope} onChange={setScope} help={STATE_NOTES[scope]} />}

			<Fields key={hover ? 'hover' : 'normal'} hover={hover} badged={badged} />
		</View>
	);
});

const Fields = observer(({ hover, badged }: {
	hover?: boolean;
	badged?: boolean;
}) => {
	const tokens = useDesignTokens();

	const shapes = currentEditorStore.sidebarShapes;
	const textKey = hover ? 'hoverText' : 'text';
	const lock = themeTextLock(shapes, !!hover);
	const lockedBy = (key: keyof ShapeTextStyle) => lock(key);
	const shown = <K extends keyof ShapeTextStyle>(key: K, own: ShapeTextStyle[K] | undefined) => {
		const locked = lock(key);

		return locked ? locked.style[key] : own;
	};

	const apply = ((): (
		name: string,
		value: any,
		options?: DrawTriggerOptions,
	) => Promise<void> => {
		return async (name, v, options) => {
			const method = hover ? 'setShapeHoverText' : 'setShapeText';
			const value: ShapeStyle = { [name]: v };
			currentEditorStore.get[method](shapes.map(s => s.id), value, options);
		};
	})();

	const applyValues = (values: Partial<ShapeText>) => {
		const method = hover ? 'setShapeHoverText' : 'setShapeText';

		currentEditorStore.get[method](shapes.map(s => s.id), values);
	};

	const text = ((): Partial<ShapeText> | undefined => {
		const res = {
			text: shown('text', commonValue(shapes, s => s[textKey]?.text)),
			size: shown('size', commonValue(shapes, s => s[textKey]?.size)),
			fixedSizePx: shown('fixedSizePx', commonValue(shapes, s => s[textKey]?.fixedSizePx)),
			color: shown('color', commonValue(shapes, s => s[textKey]?.color)),
			bgColor: shown('bgColor', commonValue(shapes, s => s[textKey]?.bgColor)),
			horizontalAlign: shown('horizontalAlign', commonValue(shapes, s => s[textKey]?.horizontalAlign)),
			verticalAlign: shown('verticalAlign', commonValue(shapes, s => s[textKey]?.verticalAlign)),
			opacity: shown('opacity', commonValue(shapes, s => s[textKey]?.opacity)),
			rotation: shown('rotation', commonValue(shapes, s => s[textKey]?.rotation)),
		};

		if (!shapes.length) return undefined;
		return res;
	})();

	// Idem `AreaThemeSection` : distingue "valeurs mixtes" de "propriété non définie partout".
	const isMixed = (key: keyof ShapeText) => {
		if (shapes.length < 2 || lockedBy(key as keyof ShapeTextStyle)) return false;
		return new Set(shapes.map(s => s[textKey]?.[key])).size > 1;
	};

	const single = shapes.length === 1 ? shapes.at(0) : undefined;
	const linkedShape = !hover && single && shapePlaceId(single) && !lock('text') ? single : undefined;
	const isLinked = linkedShape?.text?.source === PLACE_LABEL_SOURCE;

	const isCollapsed = (() => !text?.text)();

	const sizeLock = lock('size');
	const orientationLock = lock('rotation');
	const hasFixedSize = sizeLock ? sizeLock.style.fixedSizePx !== undefined : commonValue(shapes, s => s[textKey]?.fixedSizePx !== undefined);
	const keepsOrientation = orientationLock ? !!orientationLock.style.rotateWithMap : commonValue(shapes, s => !!s[textKey]?.rotateWithMap);
	const keepsReadable = orientationLock ? !!orientationLock.style.keepReadable : commonValue(shapes, s => !!s[textKey]?.keepReadable);
	const font = shown('font', commonValue(shapes, s => s[textKey]?.font));
	const fontStyle = shown('fontStyle', commonValue(shapes, s => s[textKey]?.fontStyle));
	const justify = shown('justify', commonValue(shapes, s => s[textKey]?.justify));
	const uppercase = lockedBy('uppercase') ? !!lock('uppercase')!.style.uppercase : commonValue(shapes, s => !!s[textKey]?.uppercase);
	const isMultiline = shapes.some(s => s[textKey]?.text?.includes('\n'));
	// Rotation par défaut : le cap de la vue de départ, opposé de sa `rotationDeg` (voir `showStartView`).
	const startRotation = currentEditorStore.get.mapAdapter.getStartCamera()?.rotationDeg ?? 0;
	const mapRotationDeg = Math.round((((-startRotation) % 360) + 360) % 360) % 360;
	const isCreated = !!hover && !!text?.text;

	const sizeUnit = {
		value: hasFixedSize ? 'px' : '%',
		options: [
			{ value: '%', label: '%' },
			{ value: 'px', label: 'px' },
		],
		title: '% : proportionnelle à la forme. px : taille fixe à l\'écran, quel que soit le zoom.',
		onChange: (unit: string) => apply('fixedSizePx', unit === 'px' ? DEFAULT_TEXT_FIXED_SIZE_PX : undefined),
	};

	const positionLock = lockedBy('horizontalAlign') ?? lockedBy('verticalAlign');

	return (
		<View gap="lg" className={hover ? 'normal-hover-fields--hover' : 'normal-hover-fields--normal'}>
			{isCreated && (
				<View row spread centerV>
					<View color={tokens.text.muted} size={12}>Style au survol personnalisé</View>

					<Button size="small" onClick={() => apply('text', '')}>
						Réinitialiser
					</Button>
				</View>
			)}

			<View gap="sm">
				{!!lockedBy('text') && (
					<View row spread centerV>
						<View size={13} style={{ fontWeight: 600 }}>Texte</View>
						<ThemeLockTag lock={lockedBy('text')!} />
					</View>
				)}

				<ThemeLockedControl lock={lockedBy('text')}>
					<TextContentField
						value={text?.text}
						mixed={isMixed('text')}
						bound={!!linkedShape && isLinked}
						linkedLabel={linkedShape ? (placeLabel(linkedShape) ?? text?.text ?? '') : undefined}
						onSubmit={value => apply('text', value)}
						onUnbind={linkedShape ? () => currentEditorStore.get.setShapeText(linkedShape.id, {
							text: placeLabel(linkedShape) ?? text?.text ?? '',
							source: undefined,
						}) : undefined}
						onBind={linkedShape ? () => currentEditorStore.get.setShapeText(linkedShape.id, {
							text: placeLabel(linkedShape) ?? '',
							source: PLACE_LABEL_SOURCE,
						}) : undefined}
					/>
				</ThemeLockedControl>
			</View>

			{!isCollapsed && !badged && (
				<>
					<View gap="sm">
						<Field label="Police" lockedBy={lockedBy('font')}>
							<TextFontSelect value={font} mixed={isMixed('font')} onChange={value => apply('font', value)} />
						</Field>

						<View row gap={6} centerV wrap>
							<ToolbarToggle
								label="Gras"
								lockedBy={lockedBy('fontStyle')}
								active={fontStyle === 'bold'}
								onClick={() => apply('fontStyle', fontStyle === 'bold' ? undefined : 'bold')}
							>
								<b>B</b>
							</ToolbarToggle>

							<ToolbarToggle
								label="Italique"
								lockedBy={lockedBy('fontStyle')}
								active={fontStyle === 'italic'}
								onClick={() => apply('fontStyle', fontStyle === 'italic' ? undefined : 'italic')}
							>
								<i style={{ fontFamily: 'Georgia, serif' }}>I</i>
							</ToolbarToggle>

							<ToolbarToggle
								label="Majuscules"
								lockedBy={lockedBy('uppercase')}
								active={!!uppercase}
								onClick={() => apply('uppercase', uppercase ? undefined : true)}
							>
								<span style={{ fontSize: 12, fontWeight: 600 }}>AA</span>
							</ToolbarToggle>

							<View w={1} h={24} bg={tokens.border.default} style={{ margin: '0 4px' }} />

							<ColorButton
								label="Texte"
								lockedBy={lockedBy('color')}
								value={text?.color}
								mixed={isMixed('color')}
								onChange={color => apply('color', color)}
							/>

							<ColorButton
								label="Contour"
								lockedBy={lockedBy('bgColor')}
								value={text?.bgColor}
								mixed={isMixed('bgColor')}
								onChange={color => apply('bgColor', color)}
							/>
						</View>

						<View style={{ marginTop: 6 }}>
							<Field label="Taille" lockedBy={sizeLock}>
								{hasFixedSize ? (
									<SliderInputNumberFeatureText
										property="fixedSizePx"
										initialValue={text?.fixedSizePx ?? DEFAULT_TEXT_FIXED_SIZE_PX}
										toModelValue={(v) => v}
										fromModelValue={(v) => v}
										inputProps={{ min: 1, max: SLIDER_MAX_TEXT_FIXED_SIZE_PX, inputMax: MAX_TEXT_FIXED_SIZE_PX, step: 1 }}
										mixed={isMixed('fixedSizePx')}
										apply={apply}
										unitSelect={sizeUnit}
									/>
								) : (
									<SliderInputNumberFeatureText
										property="size"
										initialValue={text?.size || 1}
										toModelValue={(v) => v / 100}
										fromModelValue={(v) => v * 100}
										inputProps={{ min: 0, max: 100, step: 1 }}
										mixed={isMixed('size')}
										apply={apply}
										unitSelect={sizeUnit}
									/>
								)}
							</Field>
						</View>
					</View>

					<View gap="sm">
						<View row spread centerV>
							<View size={13} style={{ fontWeight: 600 }}>Position</View>
							{!!positionLock && <ThemeLockTag lock={positionLock} />}
						</View>

						<View row gap="md">
							<ThemeLockedControl lock={positionLock}>
								<PositionGrid
									horizontal={isMixed('horizontalAlign') ? undefined : (text?.horizontalAlign ?? 0.5)}
									vertical={isMixed('verticalAlign') ? undefined : (text?.verticalAlign ?? 0.5)}
									onPick={(horizontalAlign, verticalAlign) => applyValues({ horizontalAlign, verticalAlign })}
								/>
							</ThemeLockedControl>

							<View size={12} color={tokens.text.muted} lineHeight={1.45} style={{ paddingTop: 4 }}>
								Un clic place le texte dans la forme. Réglage plus fin dans « Options avancées ».
							</View>
						</View>

						{isMultiline && (
							<View row spread centerV>
								<View row centerV gap={6}>
									<View size={13} style={{ fontWeight: 600 }}>Lignes</View>
									{!!lockedBy('justify') && <ThemeLockTag lock={lockedBy('justify')!} />}
								</View>

								<ThemeLockedControl lock={lockedBy('justify')}>
									<Segmented
										size="small"
										value={justify ?? (isMixed('justify') ? undefined : 'center')}
										onChange={value => apply('justify', value === 'center' ? undefined : value)}
										options={[
											{ value: 'left', label: 'Gauche' },
											{ value: 'center', label: 'Centre' },
											{ value: 'right', label: 'Droite' },
										]}
									/>
								</ThemeLockedControl>
							</View>
						)}
					</View>

					<CollapsiblePanel title="Options avancées">
						<Field label="Opacité" lockedBy={lockedBy('opacity')}>
							<SliderInputNumberFeatureText
								property="opacity"
								initialValue={text?.opacity ?? 1}
								toModelValue={v => v / 100}
								fromModelValue={v => v * 100}
								isPercent
								mixed={isMixed('opacity')}
								apply={apply}
							/>
						</Field>

						{/* Le texte au survol garde l'orientation du texte normal. */}
						{!hover && (
							<OrientationField
								lockedBy={orientationLock}
								readable
								mode={orientationModeOf({ rotation: text?.rotation, rotateWithMap: !!keepsOrientation, keepReadable: !!keepsReadable })}
								mixed={keepsOrientation === undefined || keepsReadable === undefined || isMixed('rotation')}
								effectiveRotation={text?.rotation ?? mapRotationDeg}
								onChange={applyValues}
								slider={(
									<SliderInputNumberFeatureText
										property="rotation"
										initialValue={text?.rotation ?? mapRotationDeg}
										toModelValue={v => v}
										fromModelValue={v => v}
										inputProps={{ min: 0, max: 359, step: 1, suffix: '°' }}
										apply={apply}
									/>
								)}
							/>
						)}

						<Field label="Position horizontale" lockedBy={lockedBy('horizontalAlign')}>
							<SliderInputNumberFeatureText
								property="horizontalAlign"
								initialValue={text?.horizontalAlign ?? 0.5}
								toModelValue={(v) => v / 100}
								fromModelValue={(v) => v * 100}
								isPercent
								mixed={isMixed('horizontalAlign')}
								apply={apply}
							/>
						</Field>

						<Field label="Position verticale" lockedBy={lockedBy('verticalAlign')}>
							<SliderInputNumberFeatureText
								property="verticalAlign"
								initialValue={text?.verticalAlign ?? 0.5}
								toModelValue={(v) => v / 100}
								fromModelValue={(v) => v * 100}
								isPercent
								mixed={isMixed('verticalAlign')}
								apply={apply}
							/>
						</Field>
					</CollapsiblePanel>
				</>
			)}
		</View>
	);
});

export default AreaTextSection;

const TEXT_DEFAULTS: Partial<Record<string, number>> = {
	size: 1,
	fixedSizePx: DEFAULT_TEXT_FIXED_SIZE_PX,
	opacity: 1,
	horizontalAlign: 0.5,
	verticalAlign: 0.5,
};

const SliderInputNumberFeatureText = ({
	property,
	initialValue,
	toModelValue,
	fromModelValue,
	inputProps,
	isPercent,
	mixed,
	apply,
	unitSelect,
}: {
	property: 'size' | 'fixedSizePx' | 'text' | 'horizontalAlign' | 'verticalAlign' | 'opacity' | 'rotation';
	initialValue: number;
	toModelValue: (v: number) => number;
	fromModelValue: (v: number) => number;
	inputProps?: any;
	isPercent?: boolean;
	mixed?: boolean;
	apply: (name: string, value: any, options?: DrawTriggerOptions) => Promise<void>;
	unitSelect?: React.ComponentProps<typeof SliderInputNumberFeature>['unitSelect'];
}) => {
	const startValue = React.useRef(initialValue);
	const [loading, setLoading] = React.useState(false);

	return (
		<SliderInputNumberFeature
			loading={loading}
			value={fromModelValue(initialValue)}
			resetValue={TEXT_DEFAULTS[property] === undefined ? undefined : fromModelValue(TEXT_DEFAULTS[property]!)}
			unitSelect={unitSelect}
			isPercent={isPercent}
			mixed={mixed}
			inputProps={inputProps}
			onStartChange={() => {
				startValue.current = initialValue;
			}}
			onChange={v => {
				return apply(property, toModelValue(v), {
					skipSave: true,
					skipHistory: true,
					skipConfirm: true,
				});
			}}
			onAfterChange={async v => {
				setLoading(true);

				await apply(property, toModelValue(v));

				setLoading(false);
			}}
		/>
	);
};
